import { Component } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import UserMenu from './UserMenu';

import NZ2026Home from './NZ2026Home';
import NZ2026SeatComparison from './NZ2026SeatComparison';
import VotersDemographics from './VotersDemographics';
import VotersSeats from './VotersSeats';
import PolicyView from './PolicyView';
import NZ2026Summary from './NZ2026Summary';
import PolicySummary from './PolicySummary';
import SegmentPage from "./SegmentPage"
import ReportPage from './ReportPage';
import { AccessContext } from './access/AccessContext';


const NAV_ITEMS = [
  {
    key: 'NZ2026',
    label: 'NZ 2026',
    children: [
      { value: 'NZ2026/home', label: 'Home' },
      { value: 'NZ2026/seatComparison', label: 'Vote Change' },
      { value: 'NZ2026/summary', label: 'Summary' },
    ],
  },
  {
    key: 'voters',
    label: 'Voter Breakdown',
    children: [
      { value: 'voters/demographics', label: 'Demographics' },
      { value: 'voters/seats', label: 'Profiles' },
    ],
  },
  {
    key: 'policy',
    label: 'Policy',
    children: [
      { value: 'policy/scorecard', label: 'Scorecard' },
      { value: 'policy/summary', label: 'Summary' },
    ],
  },
  {
    key: 'segments',
    label: 'Segments',
    children: [{ value: 'segments/Segments', label: 'Segments' }],
  },
  {
    key: 'report',
    label: 'Report',
    children: [{ value: 'report/electorate', label: 'Electorate' }],
  },
];

// NAV_ITEMS narrowed to the sections this user was granted. Parents whose every
// child is gated drop out entirely, so Policy simply vanishes for party users.
const grantedNav = (access) =>
  NAV_ITEMS.map((item) => ({
    ...item,
    children: item.children.filter((child) => access?.sections?.includes(child.value)),
  })).filter((item) => item.children.length > 0);

const allowedSet = (items) =>
  new Set(items.flatMap((item) => item.children.map((child) => child.value)));

const getParentItem = (items, selectedSection) =>
  items.find((item) => item.children.some((c) => c.value === selectedSection)) || items[0];

export default class Home extends Component {
  static contextType = AccessContext;

  constructor(props) {
    super(props);

    this.state = {
      // Resolved against the granted nav in render — a user who cannot open
      // NZ2026/home lands on the first section they can.
      selectedSection: 'NZ2026/home',
      constituencyMap: null,
      SA3Map: null,
      SA2Map: null,
      loading: true,
    };
  }

  componentDidMount() {
    this._onPopState = () => {
      const selectedSection = this.state.selectedSection;
      this.applyTheme(selectedSection);
    };

    window.addEventListener('popstate', this._onPopState);

    const loadFGB = async (url) => {
      const { geojson } = await import('flatgeobuf');
      const resp = await fetch(url);
      const features = [];
      for await (const feature of geojson.deserialize(resp.body)) {
        features.push(feature);
      }
      return { type: 'FeatureCollection', features };
    };

    Promise.all([
      loadFGB(process.env.PUBLIC_URL + '/NZ_maps/newZealandConstituency.fgb'),
      loadFGB(process.env.PUBLIC_URL + '/NZ_maps/newZealandSA3.fgb'),
      loadFGB(process.env.PUBLIC_URL + '/NZ_maps/newZealandSA2.fgb'),
    ])
      .then(([constituencyData, sa3Data, sa2Data]) => {
        this.setState({
          constituencyMap: constituencyData,
          SA3Map: sa3Data,
          SA2Map: sa2Data,
          loading: false,
        });
      })
      .catch((error) => {
        console.error('Error loading map data:', error);
        this.setState({ loading: false });
      });
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this._onPopState);
  }

  applyTheme = (theme) => {
    // A gated section can never be selected, however it was reached.
    if (!allowedSet(grantedNav(this.context.access)).has(theme)) return;

    this.setState({ selectedSection: theme });
  };

  handleParentChange = (parentKey) => {
    const parent = grantedNav(this.context.access).find((item) => item.key === parentKey);
    if (!parent || !parent.children.length) return;

    this.applyTheme(parent.children[0].value);
  };

  renderContent = (selectedSection) => {
    const { SA2Map, SA3Map, constituencyMap } = this.state;

    const sharedProps = {
      SA2Map,
      SA3Map,
      constituencyMap,
    };

    // Second gate: the nav is filtered, but never render a page the access
    // block does not grant even if a section string arrives another way.
    if (!allowedSet(grantedNav(this.context.access)).has(selectedSection)) return null;

    switch (selectedSection) {
      case 'NZ2026/home':
        return <NZ2026Home {...sharedProps} />;
      case 'NZ2026/seatComparison':
        return <NZ2026SeatComparison {...sharedProps} />;
      case 'NZ2026/summary':
        return <NZ2026Summary {...sharedProps} />;
      case 'voters/demographics':
        return <VotersDemographics {...sharedProps} />;
      case 'voters/seats':
        return <VotersSeats {...sharedProps} />;
      case 'policy/scorecard':
        return <PolicyView {...sharedProps} />;
      case 'policy/summary':
        return <PolicySummary {...sharedProps} />;
      case 'segments/Segments':
        return <SegmentPage {...sharedProps} />;
      case 'report/electorate':
        return <ReportPage {...sharedProps} />;
      default:
        return null;
    }
  };

  renderNav = (navItems, selectedSection) => {
    const activeParent = getParentItem(navItems, selectedSection);

    return (
      <Stack
        spacing={0.25}
        sx={{
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          py: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={0.25}
          sx={{
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            rowGap: 0.15,
            lineHeight: 1,
          }}
        >
          {navItems.map((item) => {
            const isActive = item.key === activeParent.key;
            const isDisabled = !item.children.length;

            return (
              <Button
                key={item.key}
                disableElevation
                disableRipple
                disabled={isDisabled}
                onClick={() => this.handleParentChange(item.key)}
                sx={{
                  minWidth: 'auto',
                  minHeight: 22,
                  height: 22,
                  px: 0.8,
                  py: 0,
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: isActive ? 800 : 700,
                  fontSize: '0.95rem',
                  lineHeight: 1,
                  color: isActive ? '#111827' : '#6B7280',
                  backgroundColor: 'transparent',
                  borderBottom: isActive ? '2px solid #111827' : '2px solid transparent',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#111827',
                    borderBottom: '2px solid #D1D5DB',
                  },
                  '&.Mui-disabled': {
                    color: '#9CA3AF',
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>

        <Stack
          direction="row"
          spacing={0.4}
          sx={{
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            rowGap: 0.35,
            lineHeight: 1,
          }}
        >
          {activeParent.children.map((child) => {
            const isActive = selectedSection === child.value;

            return (
              <Button
                key={child.value}
                variant={isActive ? 'contained' : 'text'}
                disableElevation
                onClick={() => this.applyTheme(child.value)}
                sx={{
                  minWidth: 'auto',
                  minHeight: 18,
                  height: 18,
                  padding: '0 8px',
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  lineHeight: 1,
                  ...(isActive
                    ? {
                        backgroundColor: '#111827',
                        color: '#FFFFFF',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#1F2937',
                        },
                      }
                    : {
                        color: '#4B5563',
                        backgroundColor: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                          borderColor: '#D1D5DB',
                        },
                      }),
                }}
              >
                {child.label}
              </Button>
            );
          })}
        </Stack>
      </Stack>
    );
  };

  render() {
  const { loading } = this.state;
  const { access, loading: accessLoading, error: accessError } = this.context;
  const HEADER_HEIGHT = 56;

  // Maps and the access block both have to land before the shell can be drawn —
  // the nav itself depends on what this user was granted.
  if (loading || accessLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const navItems = grantedNav(access);

  // Fail closed: an access lookup that failed, or a user with no sections at all,
  // gets a message rather than a blank shell or an accidentally open one.
  if (accessError || !navItems.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          gap: 1,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Box sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
          No dashboard access
        </Box>
        <Box sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
          {accessError
            ? 'We could not load your permissions. Please sign in again.'
            : 'Your account has no sections enabled. Contact your administrator.'}
        </Box>
      </Box>
    );
  }

  // Honour the current selection when it is granted, else fall back to the first
  // section this user can open.
  const selectedSection = allowedSet(navItems).has(this.state.selectedSection)
    ? this.state.selectedSection
    : navItems[0].children[0].value;

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={(theme) => ({
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 20,
          backgroundColor: theme.palette.background.paper,
          borderBottom: '1px solid #E5E7EB',
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            height: HEADER_HEIGHT,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            px: 2,
            py: 0.25,
            gap: 1.5,
            boxSizing: 'border-box',
          })}
        >
          {access.show_logo && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <img
                src={process.env.PUBLIC_URL + '/static/images/stack_logo.png'}
                alt="District Profiles"
                style={{ height: 34, objectFit: 'contain' }}
              />
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {this.renderNav(navItems, selectedSection)}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <UserMenu user={this.context.user || {}} onUserUpdated={this.context.refresh} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ height: `calc(100dvh - ${HEADER_HEIGHT + 1}px)`, overflow: 'hidden' }}>
        {this.renderContent(selectedSection)}
      </Box>
    </Box>
  );
}
}